import {
  IconBin,
  IconCheck,
  IconClose,
  IconEdit,
  MUIList,
  MUIBox,
  MUIListItem,
  MUIGrid,
  MUITypography,
  tokens,
} from "@cobee/cobee-ui";
import { IconButton } from "@material-ui/core";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutline";
import { useTranslation } from "react-i18next";

import { Routes } from "app/routes/config";
import { getLanguage } from "infrastructure/translate/localization";
import { Money } from "v2/domain/common/entities/Money";
import { SplitTreatments } from "@splitsoftware/splitio-react";
import featureFlagsDictionary from "infrastructure/split/feature-flags-dictionary";
import Datetime, { DateFormat } from "v2/domain/common/entities/Datetime";
import analytics from "app/segment";
import { EventNames } from "v2/infrastructure/common/eventsDictionary/EventNames";
import { CountryIsoCode } from "v2/domain/common/enums/CountryISOCode";
import {
  ConsumableFromLabel,
  RenewalLabel,
  NoAmountLabel,
} from "../BenefitBagLabel";

const periodicityByDurationInPayrolls: Record<number, string> = {
  0: "punctual",
  1: "monthly",
  12: "annual",
};

export default function BenefitBagCard({
  bag,
  benefitModelId,
  removeBag,
  country,
  companyId,
}: {
  bag;
  benefitModelId;
  removeBag;
  country: CountryIsoCode;
  companyId: string;
}) {
  const { t } = useTranslation();
  const {
    accumulableAtPeriodEnd,
    allowedFor,
    limitAmount,
    merchantGroupBundles = [],
    periodDurationInPayrolls,
    renewableAt,
    individualAdjustmentsCount,
    consumableFrom,
    benefitBagConfigId,
  } = bag;

  const isPunctual = periodDurationInPayrolls === 0;
  const isReadyToBeConsumed = new Date() > new Date(consumableFrom);
  const countriesWithAccumulationInPunctualDeliveries = [CountryIsoCode.MEXICO];
  const shouldShowAccumulationInfo =
    countriesWithAccumulationInPunctualDeliveries.includes(country) ||
    !isPunctual;

  const merchantGroupBundleNames = merchantGroupBundles.map(
    (merchantGroupBundle) =>
      merchantGroupBundle?.languages[getLanguage()]?.name ||
      merchantGroupBundle?.languages?.en?.name
  );

  const translatedBenefits = allowedFor.map(({ benefit }) => {
    return t(`v2.common.benefit-type.${benefit}`);
  });

  const legacyCompleteAllowedFor = [
    ...translatedBenefits,
    ...merchantGroupBundleNames,
  ].join(", ");

  const completeAllowedFor = [
    ...translatedBenefits,
    ...merchantGroupBundleNames,
  ].map((benefit: string) => (
    <MUIListItem
      key={benefit}
      sx={{
        display: "list-item",
        marginRight: "25px",
        padding: 0.25,
        width: "calc(50% - 25px)",
      }}
    >
      <MUITypography
        variant="body2"
        color={tokens.palette.grey["900"]}
        sx={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {benefit}
      </MUITypography>
    </MUIListItem>
  ));

  const infoStyles = {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
    color: "#6B6B6B",
  };

  const valueStyles = {
    ...infoStyles,
    ...{ color: tokens.palette.grey[900], marginLeft: " 4px" },
  };

  const accumulationMessage = accumulableAtPeriodEnd
    ? {
        icon: <IconCheck color={tokens.palette.grey["900"]} />,
        label: t("v2.common.yes"),
      }
    : {
        icon: <IconClose color={tokens.palette.grey["900"]} />,
        label: t("v2.common.no"),
      };

  const handleBagEdit = async () => {
    await analytics.track(
      EventNames.DELIVERY_EDIT_BUTTON_CLICKED,
      {
        periodicity: periodicityByDurationInPayrolls[periodDurationInPayrolls],
        accumulation: shouldShowAccumulationInfo
          ? accumulableAtPeriodEnd
          : undefined,
        benefits: allowedFor.map(({ benefit }) => benefit),
      },
      undefined,
      () => {
        window.location.assign(
          `${window.location.protocol}//${
            window.location.host
          }${Routes.EDIT_BENEFIT_BAG(benefitModelId, benefitBagConfigId)}`
        );
      }
    );
  };

  const handleBagDelete = async () => {
    await analytics.track(
      EventNames.DELIVERY_DELETE_BUTTON_CLICKED,
      {
        periodicity: periodicityByDurationInPayrolls[periodDurationInPayrolls],
        benefits: allowedFor.map(({ benefit }) => benefit),
        accumulation: shouldShowAccumulationInfo
          ? accumulableAtPeriodEnd
          : undefined,
      },
      undefined,
      () => {
        removeBag();
      }
    );
  };

  return (
    <SplitTreatments
      names={[featureFlagsDictionary.BI_1434]}
      attributes={{
        companyId,
        countryISOCode: country,
      }}
    >
      {({ isReady, treatments }) => {
        if (
          isReady &&
          treatments[featureFlagsDictionary.BI_1434].treatment === "on"
        ) {
          return (
            <MUIGrid
              container
              sx={{
                backgroundColor: tokens.palette.common.white,
                borderRadius: "12px",
                boxShadow: tokens.shadows.cardSoftShadow,
                padding: "24px",
                height: "100%",
                flexWrap: "no-wrap",
              }}
              alignItems="flex-start"
              flexDirection="column"
              data-testid={bag?.name}
            >
              <MUIGrid container alignItems="center">
                <MUIGrid item xs>
                  <MUITypography
                    color={tokens.palette.grey[900]}
                    style={{
                      fontSize: "16px",
                      lineHeight: "24px",
                      fontWeight: "500",
                    }}
                    variant="body2"
                  >
                    {bag?.name}
                  </MUITypography>
                </MUIGrid>

                <MUIGrid item pr={2}>
                  <IconButton
                    data-testid="edit-icon"
                    onClick={handleBagEdit}
                    size="small"
                  >
                    <IconEdit color={tokens.palette.secondary.main} size={20} />
                  </IconButton>
                </MUIGrid>

                <MUIGrid item>
                  <IconButton
                    data-testid="remove-icon"
                    onClick={handleBagDelete}
                    size="small"
                  >
                    <IconBin color={tokens.palette.secondary.main} size={20} />
                  </IconButton>
                </MUIGrid>
              </MUIGrid>
              <MUIGrid container display="flex" flexDirection="column" gap={1}>
                {!isReadyToBeConsumed && (
                  <ConsumableFromLabel
                    text={t("v2.delivery.available-at", {
                      date: new Datetime(consumableFrom).toDatetimeString(
                        DateFormat.BULLETED_DD_MMM_YYYY
                      ),
                    })}
                  />
                )}
                {isReadyToBeConsumed && !isPunctual && (
                  <RenewalLabel
                    text={t("v2.delivery.next-renewal", {
                      date: new Datetime(renewableAt).toDatetimeString(
                        DateFormat.BULLETED_DD_MMM_YYYY
                      ),
                    })}
                  />
                )}
                {isReadyToBeConsumed && isPunctual && (
                  <RenewalLabel text={t("v2.delivery.not-renewable")} />
                )}
                {limitAmount.amountInCents === 0 && (
                  <NoAmountLabel text={t("v2.delivery.no-amount-delivery")} />
                )}
              </MUIGrid>
              <MUIGrid
                container
                display="flex"
                justifyContent="space-between"
                marginTop={1}
              >
                <MUIGrid
                  item
                  display="flex"
                  flexDirection="column"
                  flexGrow={1}
                >
                  <MUITypography
                    color={tokens.palette.grey["700"]}
                    variant="body2"
                  >
                    {t("common-messages.amount")}
                  </MUITypography>
                  <MUITypography
                    color={
                      limitAmount.amountInCents > 0
                        ? tokens.palette.grey["900"]
                        : tokens.palette.error.dark
                    }
                    variant="body2"
                  >{`${new Money(limitAmount).toString(
                    "$0,0.00"
                  )}`}</MUITypography>
                </MUIGrid>
                <MUIGrid
                  item
                  display="flex"
                  flexDirection="column"
                  flexGrow={1}
                >
                  <MUITypography
                    variant="body2"
                    color={tokens.palette.grey["700"]}
                  >
                    {t("v2.delivery.recurrence")}
                  </MUITypography>
                  <MUITypography
                    variant="body2"
                    color={tokens.palette.grey["900"]}
                  >
                    {t(
                      `common-messages.${periodicityByDurationInPayrolls[periodDurationInPayrolls]}`
                    )}
                  </MUITypography>
                </MUIGrid>
                {shouldShowAccumulationInfo && (
                  <MUIGrid
                    item
                    display="flex"
                    flexDirection="column"
                    flexGrow={1}
                  >
                    <MUITypography
                      variant="body2"
                      color={tokens.palette.grey["700"]}
                    >
                      {t("v2.common.accumulation")}
                    </MUITypography>
                    <MUIBox display="flex" alignItems="center">
                      <MUITypography
                        variant="body2"
                        color={tokens.palette.grey["900"]}
                      >
                        {accumulationMessage.icon} {accumulationMessage.label}
                      </MUITypography>
                    </MUIBox>
                  </MUIGrid>
                )}
              </MUIGrid>
              {individualAdjustmentsCount > 0 && (
                <MUIGrid
                  container
                  display="flex"
                  flexDirection="column"
                  flexGrow={1}
                  marginTop={1}
                >
                  <MUITypography
                    color={tokens.palette.grey["700"]}
                    variant="body2"
                  >
                    {t("v2.delivery.current-period-individual-adjustments")}
                  </MUITypography>
                  <MUITypography
                    color={tokens.palette.grey["900"]}
                    variant="body2"
                  >
                    {t(
                      `v2.delivery.employees.${
                        individualAdjustmentsCount === 1 ? "one" : "other"
                      }`,
                      {
                        employees: individualAdjustmentsCount,
                      }
                    )}
                  </MUITypography>
                </MUIGrid>
              )}
              <div
                style={{
                  borderBottom: `1px solid ${tokens.palette.grey["100"]}`,
                  marginBottom: 8,
                  marginTop: 8,
                  width: "100%",
                }}
              />
              <MUIGrid
                container
                marginTop={1}
                display="flex"
                flexDirection="column"
              >
                <MUITypography
                  variant="body2"
                  color={tokens.palette.grey["700"]}
                >
                  {t("benefits-section.benefits")}
                </MUITypography>
                <MUIList
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    listStyleType: "disc",
                    pb: 0,
                    pl: 3,
                    pr: 3,
                    pt: 0,
                    width: "fill-available",
                  }}
                >
                  {completeAllowedFor}
                </MUIList>
              </MUIGrid>
            </MUIGrid>
          );
        }
        return (
          <div
            style={{
              backgroundColor: tokens.palette.common.white,
              borderRadius: "24px",
              padding: "24px",
              boxShadow: "1px 1px 10px rgba(151, 151, 151, 0.2)",
              height: "100%",
            }}
          >
            <MUIGrid
              container
              justifyContent="space-between"
              alignItems="center"
            >
              <MUIGrid item>
                <span
                  style={{
                    fontSize: "12px",
                    marginBottom: "4px",
                    color: "#6B6B6B",
                  }}
                >
                  {t(
                    `subsidy-section.common-messages.${
                      periodicityByDurationInPayrolls[
                        periodDurationInPayrolls
                      ] || ""
                    }-bag`
                  )}
                </span>
              </MUIGrid>
              <MUIGrid item>
                <IconButton
                  data-testid="remove-icon"
                  onClick={handleBagDelete}
                  size="small"
                  style={{ color: tokens.palette.secondary.main }}
                >
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </MUIGrid>
            </MUIGrid>
            <MUIGrid item xs={12}>
              <p
                color={tokens.palette.grey[900]}
                style={{
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: "500",
                  marginBottom: "4px",
                }}
              >
                {bag?.name}
              </p>
            </MUIGrid>
            <MUIGrid item container xs={12}>
              <p style={infoStyles}>{t("common-messages.amount")}:</p>
              <p style={valueStyles}>{`${new Money(
                limitAmount
              ).toString()}`}</p>
            </MUIGrid>
            <MUIGrid item container xs={12}>
              <p style={infoStyles}>{t("benefits-section.benefits")}:</p>
              <p style={valueStyles}>{legacyCompleteAllowedFor}</p>
            </MUIGrid>
          </div>
        );
      }}
    </SplitTreatments>
  );
}
